'use client';

import { phoneRegex, zipRegex } from '@/lib/validations/address';

import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Crown,
  Edit3,
  Globe,
  Home,
  MapPin,
  Phone,
  Plus,
  Sparkles,
  Star,
  Trash2,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/EmptyState';
import { useAddresses } from '@/hooks/useAddresses';
import { Address, AddressAddressTypeEnum, CreateAddressDtoAddressTypeEnum, UpdateAddressDto, UpdateAddressDtoAddressTypeEnum } from '@/lib/Apis';
import { useNotification } from '@/providers/NotificationProvider';


const addressFormSchema = z.object({
  fullName: z.string().min(2, 'Please enter a full name').max(100, 'Full name must be at most 100 characters'),
  phone: z.string()
    .min(1, 'Phone number is required')
    .refine(val => phoneRegex.test(val.trim()), {
      message: 'Invalid phone number. Include international calling code (e.g., +1 or +41)',
    }),
  addressLine1: z.string().min(4, 'Address line 1 is required').max(200, 'Address must be at most 200 characters'),
  addressLine2: z.string().max(200, 'Address must be at most 200 characters').optional(),
  city: z.string().min(2, 'City is required').max(100, 'City must be at most 100 characters'),
  state: z.string().min(2, 'State / Region is required').max(100, 'State must be at most 100 characters'),
  zipCode: z.string()
    .min(1, 'Postal code is required')
    .max(20, 'ZIP code must be at most 20 characters')
    .refine(val => zipRegex.test(val), {
      message: 'Please enter a valid ZIP code (e.g., 10001 or 10001-6789)',
    }),
  country: z.string().min(2, 'Country is required').max(100, 'Country must be at most 100 characters'),
  addressType: z.enum(['Billing', 'Shipping', 'Both']).default('Shipping'),
  isDefault: z.boolean().optional(),
});

type AddressFormValues = z.infer<typeof addressFormSchema>;

const FORM_DEFAULTS: AddressFormValues = {
  fullName: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'United States',
  addressType: AddressAddressTypeEnum.Both,
  isDefault: false,
};

function formatAddressSnippet(address: Address) {
  return [address.street1, address.street2, `${address.city}, ${address.state} ${address.zip}`, address.country]
    .filter(Boolean)
    .join(' • ');
}

function getAddressTypeCopy(type?: AddressAddressTypeEnum) {
  switch (type) {
    case 'Billing':
      return { label: 'Billing', icon: <Home className="h-4 w-4" /> };
    case 'Both':
      return { label: 'Billing & Shipping', icon: <Globe className="h-4 w-4" /> };
    case 'Shipping':
    default:
      return { label: 'Shipping', icon: <MapPin className="h-4 w-4" /> };
  }
}

export function AddressesScreen() {
  const {
    addresses,
    defaultAddress,
    isLoading,
    error,
    addAddress,
    updateAddress,
    removeAddress,
    markAsDefault,
    refresh,
  } = useAddresses();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const notification = useNotification();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddressFormValues>({
    resolver: zodResolver(addressFormSchema),
    defaultValues: FORM_DEFAULTS,
  });

  useEffect(() => {
    if (editingAddress) {
      reset({
        fullName: editingAddress.name,
        phone: editingAddress.phone,
        addressLine1: editingAddress.street1,
        addressLine2: editingAddress.street2,
        city: editingAddress.city,
        state: editingAddress.state,
        zipCode: editingAddress.zip,
        country: editingAddress.country,
        addressType: editingAddress.addressType as AddressAddressTypeEnum,
        isDefault: editingAddress.isDefault ?? false,
      });
    } else {
      reset(FORM_DEFAULTS);
    }
  }, [editingAddress, reset]);

  const openCreateModal = () => {
    setEditingAddress(null);
    setIsModalOpen(true);
  };

  const openEditModal = (address: Address) => {
    setEditingAddress(address);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Helper function to normalize address fields for comparison
  const normalizeAddress = (addr: { street1: string; city: string; state: string; zip: string }) => {
    return {
      street1: addr.street1.trim().toLowerCase().replace(/\s+/g, ' '),
      city: addr.city.trim().toLowerCase(),
      state: addr.state.trim().toLowerCase(),
      zip: addr.zip.trim().replace(/\s+/g, ''),
    };
  };

  // Check for duplicate address
  const isDuplicateAddress = (values: AddressFormValues, excludeId?: string) => {
    const normalized = normalizeAddress({
      street1: values.addressLine1,
      city: values.city,
      state: values.state,
      zip: values.zipCode,
    });

    return addresses.some((addr) => {
      if (excludeId && addr.id === excludeId) return false;
      const existing = normalizeAddress(addr);
      return (
        existing.street1 === normalized.street1 &&
        existing.city === normalized.city &&
        existing.state === normalized.state &&
        existing.zip === normalized.zip
      );
    });
  };

  const onSubmit = async (values: AddressFormValues) => {
    // Check for duplicate address when adding new address
    if (!editingAddress && isDuplicateAddress(values)) {
      const confirmAdd = window.confirm(
        'An address with this street, city, state, and ZIP already exists.\n\nDo you want to add it anyway?'
      );
      if (!confirmAdd) return;
    }

    const payload: UpdateAddressDto = {
      name: values.fullName,
      phone: values.phone,
      street1: values.addressLine1,
      street2: values.addressLine2 || '',
      city: values.city,
      state: values.state,
      zip: values.zipCode,
      country: values.country,
      isDefault: values.isDefault ?? false,
      addressType: values.addressType as UpdateAddressDtoAddressTypeEnum,
    };

    try {
      if (editingAddress) {
        const response = await updateAddress(editingAddress.id, payload);
        if (response) {
          notification.success('Address updated successfully');
          closeModal();
        } else {
          notification.error('Unable to update address');
        }
      } else {
        const response = await addAddress({
          name: values.fullName,
          phone: values.phone,
          street1: values.addressLine1,
          street2: values.addressLine2 || '',
          city: values.city,
          state: values.state,
          zip: values.zipCode,
          country: values.country,
          addressType: values.addressType as CreateAddressDtoAddressTypeEnum,
          isDefault: addresses.length === 0 || (values.isDefault ?? false),
        });
        if (response) {
          notification.success('Address added successfully');
          closeModal();
        } else {
          notification.error('Failed to add address');
        }
      }
    } catch (error: any) {
      console.error('Error saving address:', error);
      notification.error(
        error?.response?.data?.message || error?.message || 'Failed to save address. Please try again.'
      );
    }
  };


  const handleDelete = async (address: Address) => {
    const confirmDelete = window.confirm(
      `Remove ${address.name}'s address?\nYou can add it back at any time.`
    );
    if (!confirmDelete) return;

    try {
      await removeAddress(address.id);
      notification.success('Address removed successfully');
    } catch (error) {
      console.error('Error in handleDelete:', error);
      notification.error(
        error instanceof Error ? error.message : 'Failed to remove address. Please try again.',
      );
    }
  };

  const handleSetDefault = async (address: Address) => {
    try {
      await markAsDefault(address.id);
      notification.success(`${address.name} is now your default address`);
    } catch (error) {
      notification.error(
        error instanceof Error ? error.message : 'Failed to set default address. Try again.',
      );
    }
  };

  const stats = useMemo(() => {
    const shippingCount = addresses.filter((address) => address.addressType !== 'Billing').length;
    const billingCount = addresses.filter((address) => address.addressType !== 'Shipping').length;
    return [
      {
        id: 'total',
        label: 'Saved addresses',
        value: addresses.length,
        helper: 'All delivery and billing locations',
      },
      {
        id: 'shipping',
        label: 'Shipping ready',
        value: shippingCount,
        helper: 'Optimized for fast dispatch',
      },
      {
        id: 'billing',
        label: 'Billing profiles',
        value: billingCount,
        helper: 'Simplifies checkout',
      },
    ];
  }, [addresses]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="container mx-auto px-4 pb-16 pt-10">
        <div className="rounded-3xl border border-slate-200 bg-white/95 p-6 shadow-xl shadow-primary-50">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-1 text-xs font-semibold uppercase tracking-[0.32em]">
                <MapPin className="h-4 w-4" />
                Address book
              </div>
              <h1 className="text-3xl font-semibold md:text-4xl">Manage where your care arrives</h1>
              <p className="max-w-2xl text-sm text-slate-600">
                Save home, office, or loved ones&apos; addresses and choose a default for faster checkout and delivery.
              </p>
              <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2">
                  <Sparkles className="h-4 w-4 text-hprimary-600" />
                  Default: {defaultAddress ? defaultAddress.name : 'Not set'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">

              <Button
                variant="outline-solid"
                size="md"
                className="border-slate-300 text-slate-800 hover:bg-white"
                onClick={openCreateModal}
              >
                <Plus className="h-5 w-5" />
                Add address
              </Button>
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {stats.map((stat) => (
              <div
                key={stat.id}
                className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-600"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                  {stat.label}
                </p>
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-slate-800">{stat.helper}</p>
                  <span className="text-xl font-semibold text-slate-900">{stat.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10">
          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="h-56 animate-pulse rounded-3xl border border-slate-100 bg-white"
                />
              ))}
            </div>
          ) : error ? (
            <div className="rounded-3xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">
              {error.message}
            </div>
          ) : addresses.length === 0 ? (
            <div className="rounded-3xl border border-slate-100 bg-white p-12 shadow-xs">
              <EmptyState
                icon={MapPin}
                title="No addresses yet"
                description="Save a shipping or billing address to speed through checkout the next time you order."
                actionLabel="Add your first address"
                onAction={openCreateModal}
              />
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {addresses.map((address) => {
                const typeCopy = getAddressTypeCopy(address.addressType);
                return (
                  <motion.div
                    key={address.id}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="group relative flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-xs transition hover:-translate-y-1 hover:border-hprimary-200 hover:shadow-lg"
                  >
                    {address.isDefault && (
                      <span className="absolute right-6 top-6 inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                        <Crown className="h-4 w-4" />
                        Default
                      </span>
                    )}
                    <div className="flex items-center gap-3">
                      <span className="rounded-full bg-hprimary-50 p-3 text-hprimary-600">
                        <User className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="text-base font-semibold text-slate-900">
                          {address.name}
                        </p>
                        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                          {typeCopy.label}
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-1 flex-col gap-3 text-sm text-slate-600">
                      <p>{formatAddressSnippet(address)}</p>
                      <p className="inline-flex items-center gap-2 text-slate-500">
                        <Phone className="h-4 w-4 text-slate-400" />
                        {address.phone || 'Not provided'}
                      </p>
                    </div>

                    <div className="mt-6 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => openEditModal(address)}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-sm font-medium text-slate-600 transition hover:border-hprimary-300 hover:text-hprimary-600"
                      >
                        <Edit3 className="h-4 w-4" />
                        Edit
                      </button>
                      <div className="flex items-center gap-2">
                        {!address.isDefault && (
                          <button
                            type="button"
                            onClick={() => handleSetDefault(address)}
                            className="inline-flex items-center gap-2 rounded-full border border-amber-200 px-3 py-1 text-sm font-semibold text-amber-600 transition hover:border-amber-300 hover:text-amber-700"
                          >
                            <Star className="h-4 w-4" />
                            Make default
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDelete(address)}
                          className="inline-flex items-center gap-2 rounded-full border border-red-200 px-3 py-1 text-sm font-semibold text-red-600 transition hover:border-red-300 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingAddress ? 'Edit address' : 'Add new address'}
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Full name"
              placeholder="Taylor Pharmacist"
              {...register('fullName')}
              maxLength={100}
              error={errors.fullName?.message}
            />
            <Input
              label="Phone number"
              placeholder="+1 415 555 0100"
              {...register('phone')}
              error={errors.phone?.message}
            />
          </div>
          <Input
            label="Address line 1"
            placeholder="123 Wellness Avenue"
            {...register('addressLine1')}
            maxLength={200}
            error={errors.addressLine1?.message}
          />
          <Input
            label="Address line 2 (optional)"
            placeholder="Suite, apartment, etc."
            {...register('addressLine2')}
            maxLength={200}
            error={errors.addressLine2?.message}
          />
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="City"
              placeholder="Seattle"
              {...register('city')}
              maxLength={100}
              error={errors.city?.message}
            />
            <Input
              label="State / Region"
              placeholder="Washington"
              {...register('state')}
              maxLength={100}
              error={errors.state?.message}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Postal code"
              placeholder="98101"
              {...register('zipCode')}
              maxLength={20}
              error={errors.zipCode?.message}
            />
            <Input
              label="Country"
              placeholder="United States"
              {...register('country')}
              maxLength={100}
              error={errors.country?.message}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-2 rounded-2xl border border-slate-200 p-4">
              <span className="text-sm font-semibold text-slate-700">Address type</span>
              <select
                {...register('addressType')}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-hprimary-400 focus:outline-hidden focus:ring-2 focus:ring-hprimary-500/20"
              >
                <option value="Shipping">Shipping</option>
                <option value="Billing">Billing</option>
                <option value="Both">Billing & Shipping</option>
              </select>
            </label>
            <label className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
              <span>Set as default address</span>
              <input
                type="checkbox"
                {...register('isDefault')}
                className="h-4 w-4 rounded-sm border-hprimary-300 text-hprimary-600 focus:ring-hprimary-500"
              />
            </label>
          </div>

          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="outline-solid" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {editingAddress ? 'Save changes' : 'Save address'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
